import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import toast from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';

// Componentes de páginas
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import OrdersPage from './pages/OrdersPage';
import OrderDetailPage from './pages/OrderDetailPage';
import CreateOrderPage from './pages/CreateOrderPage';
import EditOrderPage from './pages/EditOrderPage';
import UsersPage from './pages/UsersPage';
import CompanyConfigPage from './pages/CompanyConfigPage';
import ProfilePage from './pages/ProfilePage';
import RoleManagementPage from './pages/RoleManagementPage';
import APIConfigPage from './pages/APIConfigPage';
import SiigoCredentialsPage from './pages/SiigoCredentialsPage';
import SiigoInvoicesPage from './pages/SiigoInvoicesPage';
import SiigoStartDateConfigPage from './pages/SiigoStartDateConfigPage';
import BillingPage from './pages/BillingPage';
import ShippingGuidesPage from './pages/ShippingGuidesPage';
import CustomerCreditPage from './pages/CustomerCreditPage';
import BankConfigPage from './pages/BankConfigPage';
import PackagingPage from './pages/PackagingPage';
import DeliveryMethodsPage from './pages/DeliveryMethodsPage';
import ProductsPage from './pages/ProductsPage';
import CarriersManagementPage from './pages/CarriersManagementPage';
import QuotationsPage from './pages/QuotationsPage';
import CustomersPage from './pages/CustomersPage';
import InventoryBillingPage from './pages/InventoryBillingPage';

import OverpaymentsPage from './pages/OverpaymentsPage';
import ReadyToDeliverPage from './pages/ReadyToDeliverPage';
import LogisticsHandoffPage from './pages/LogisticsHandoffPage';
import TreasuryAuditPage from './pages/TreasuryAuditPage';
import CashBoxDashboard from './pages/CashBoxDashboard';
import PackagingProgressPage from './pages/PackagingProgressPage';
import PackagingReadOnlyDetailPage from './pages/PackagingReadOnlyDetailPage';
import PostventaPage from './pages/PostventaPage';
import PostventaAnalyticsPage from './pages/PostventaAnalyticsPage';
import EvidenceGalleryPage from './pages/EvidenceGalleryPage';
import AutomationDashboardPage from './pages/AutomationDashboardPage';
import InventoryManagementPage from './pages/InventoryManagementPage';
import MixturesAuditPage from './pages/MixturesAuditPage';
import QRGeneratorPage from './pages/QRGeneratorPage';
import MerchandiseReceptionPage from './pages/MerchandiseReceptionPage';
import SupplierCodesPage from './pages/SupplierCodesPage';
import ExecutiveDashboardPage from './pages/ExecutiveDashboardPage';
import FinancialClosurePage from './pages/FinancialClosurePage';
import ExpensesPage from './pages/ExpensesPage';
import OperationalMetricsPage from './pages/OperationalMetricsPage';
import BancolombiaMovementsPage from './pages/BancolombiaMovementsPage';
import UserManualPage from './pages/UserManualPage';
import MessengerTrackingPage from './pages/MessengerTrackingPage';
import SpecialInventoryPage from './pages/SpecialInventoryPage';
import InternalPOSPage from './pages/InternalPOSPage';
import InternalOrdersHistoryPage from './pages/InternalOrdersHistoryPage';
import AttendancePage from './pages/AttendancePage';
import AttendanceKiosk from './pages/AttendanceKiosk';
import AdminAttendancePage from './pages/AdminAttendancePage';
import AlertsPage from './pages/AlertsPage';
import RoutePage from './pages/RoutePage';
import InternalBagPage from './pages/InternalBagPage';
import RouteCreationPage from './pages/RouteCreationPage';
import MessengerRoutePage from './pages/MessengerRoutePage';
import MessengerDashboard from './pages/MessengerDashboard';
import ValidateReturnsPage from './pages/ValidateReturnsPage';
import RouteSettlementPage from './pages/RouteSettlementPage';
import LiveTrackingPage from './pages/LiveTrackingPage';
import PendingRCsPage from './pages/PendingRCsPage';
import PQRDashboardPage from './pages/PQRDashboardPage';
import SatisfactionSurveyPage from './pages/SatisfactionSurveyPage';
import SurveyResultsPage from './pages/SurveyResultsPage';
import SurveyQRPrintPage from './pages/SurveyQRPrintPage';
import AnniversaryCampaignPage from './pages/AnniversaryCampaignPage';
import MarketingAnalyticsPage from './pages/MarketingAnalyticsPage';

// Componentes de layout
import Layout from './components/Layout';
import LoadingSpinner from './components/LoadingSpinner';

// Componente para rutas protegidas
const ProtectedRoute = ({ children, requiredRole }) => {
  const { isAuthenticated, isLoading, hasPermission, user } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRole && !hasPermission(requiredRole)) {
    console.log('DEBUG: ProtectedRoute Redirecting to /dashboard', {
      requiredRole,
      userRole: user?.role,
      hasPermission: hasPermission(requiredRole)
    });
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Componente para rutas públicas (solo accesibles si no está autenticado)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Componente principal de rutas
const AppRoutes = () => {
  const { user } = useAuth();
  const roleLower = String(user?.role || '').toLowerCase();
  const rolesAdv = Array.isArray(user?.roles) ? user.roles.map(r => String(r.role_name || '').toLowerCase()) : [];
  const hasRole = (names) => names.some(n => roleLower === n || rolesAdv.includes(n));

  // Solo es empacador puro si tiene rol de empacador Y NO tiene roles administrativos
  const hasPackagingRole = hasRole(['empacador', 'empaque', 'packaging']);
  const hasAdminRoles = hasRole(['admin', 'gerente', 'facturador', 'cartera', 'logistica']);
  const isEmpacador = hasPackagingRole && !hasAdminRoles;
  const isMensajero = hasRole(['mensajero']);

  return (
    <Routes>
      {/* Rutas públicas */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Rutas PÚBLICAS — Encuesta de satisfacción (sin login) */}
      <Route path="/encuesta/:tipo" element={<SatisfactionSurveyPage />} />
      <Route path="/encuesta-qr/:tipo" element={<SurveyQRPrintPage />} />
      <Route path="/kiosco" element={<AttendanceKiosk />} />

      {/* Rutas protegidas */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        {/* Dashboard - accesible para todos los roles */}
        <Route
          index
          element={
            isEmpacador ? <Navigate to="/packaging" replace /> :
              isMensajero ? <Navigate to="/messenger-dashboard" replace /> :
                <Navigate to="/dashboard" replace />
          }
        />
        <Route
          path="dashboard"
          element={
            isEmpacador ? <Navigate to="/packaging" replace /> :
              isMensajero ? <Navigate to="/messenger-dashboard" replace /> :
                <DashboardPage />
          }
        />

        {/* Dashboard Ejecutivo - solo admin/gerente */}
        <Route
          path="admin/dashboard"
          element={
            <ProtectedRoute requiredRole={['admin', 'gerente']}>
              <ExecutiveDashboardPage />
            </ProtectedRoute>
          }
        />

        {/* Pedidos - accesible para todos los roles */}
        <Route path="orders" element={isEmpacador ? <Navigate to="/packaging" replace /> : <OrdersPage />} />
        <Route path="orders/create" element={<CreateOrderPage />} />
        <Route path="orders/:id" element={<OrderDetailPage />} />
        <Route
          path="orders/:id/edit"
          element={
            <ProtectedRoute requiredRole="admin">
              <EditOrderPage />
            </ProtectedRoute>
          }
        />

        {/* Facturación - admin y facturador */}
        <Route
          path="billing"
          element={
            <ProtectedRoute requiredRole={['admin', 'facturador', 'facturacion']}>
              <BillingPage />
            </ProtectedRoute>
          }
        />

        {/* Facturas SIIGO - admin y facturador */}
        <Route
          path="siigo-invoices"
          element={
            <ProtectedRoute requiredRole={['admin', 'facturador', 'facturacion']}>
              <SiigoInvoicesPage />
            </ProtectedRoute>
          }
        />

        {/* Usuarios - solo admin */}
        <Route
          path="users"
          element={
            <ProtectedRoute requiredRole="admin">
              <UsersPage />
            </ProtectedRoute>
          }
        />

        {/* Gestión de Roles y Permisos - solo admin */}
        <Route
          path="roles-management"
          element={
            <ProtectedRoute requiredRole="admin">
              <RoleManagementPage />
            </ProtectedRoute>
          }
        />

        {/* Configuración de Empresa - solo admin */}
        <Route
          path="company-config"
          element={
            <ProtectedRoute requiredRole="admin">
              <CompanyConfigPage />
            </ProtectedRoute>
          }
        />

        {/* Configuración de APIs - solo admin */}
        <Route
          path="api-config"
          element={
            <ProtectedRoute requiredRole="admin">
              <APIConfigPage />
            </ProtectedRoute>
          }
        />

        {/* Credenciales SIIGO - solo admin */}
        <Route
          path="siigo-credentials"
          element={
            <ProtectedRoute requiredRole="admin">
              <SiigoCredentialsPage />
            </ProtectedRoute>
          }
        />

        {/* Configuración de Fecha de Inicio SIIGO - solo admin */}
        <Route
          path="siigo-start-date-config"
          element={
            <ProtectedRoute requiredRole="admin">
              <SiigoStartDateConfigPage />
            </ProtectedRoute>
          }
        />



        {/* Crédito de Clientes - solo admin */}
        <Route
          path="customer-credit"
          element={
            <ProtectedRoute requiredRole="admin">
              <CustomerCreditPage />
            </ProtectedRoute>
          }
        />

        {/* Configuración Banco - solo admin */}
        <Route
          path="bank-config"
          element={
            <ProtectedRoute requiredRole="admin">
              <BankConfigPage />
            </ProtectedRoute>
          }
        />

        {/* Sistema de Empaque - admin y empaque */}
        <Route
          path="packaging"
          element={
            <ProtectedRoute requiredRole={['admin', 'logistica', 'empaque', 'empacador']}>
              <PackagingPage />
            </ProtectedRoute>
          }
        />

        {/* Métodos de Envío - solo admin */}
        <Route
          path="delivery-methods"
          element={
            <ProtectedRoute requiredRole="admin">
              <DeliveryMethodsPage />
            </ProtectedRoute>
          }
        />

        {/* Productos y Códigos de Barras - admin y logística */}
        <Route
          path="products"
          element={
            <ProtectedRoute requiredRole={['admin', 'logistica']}>
              <ProductsPage />
            </ProtectedRoute>
          }
        />

        {/* Mapeo de Códigos de Proveedor - admin y logística */}
        <Route
          path="supplier-codes"
          element={
            <ProtectedRoute requiredRole={['admin', 'logistica']}>
              <SupplierCodesPage />
            </ProtectedRoute>
          }
        />

        {/* Gestión de Transportadoras - admin y logística */}
        <Route
          path="carriers"
          element={
            <ProtectedRoute requiredRole={['admin', 'logistica']}>
              <CarriersManagementPage />
            </ProtectedRoute>
          }
        />

        {/* Sistema de Cotizaciones - admin y facturador */}
        <Route
          path="quotations"
          element={
            <ProtectedRoute requiredRole={['admin', 'facturador', 'facturacion']}>
              <QuotationsPage />
            </ProtectedRoute>
          }
        />

        {/* Gestión de Clientes - admin y facturador */}
        <Route
          path="customers"
          element={
            <ProtectedRoute requiredRole={['admin', 'facturador', 'facturacion']}>
              <CustomersPage />
            </ProtectedRoute>
          }
        />


        {/* Sobrepagos - Admin y Cartera */}
        <Route
          path="overpayments"
          element={
            <ProtectedRoute requiredRole={['admin', 'cartera']}>
              <OverpaymentsPage />
            </ProtectedRoute>
          }
        />

        {/* Recaudos de Ruta - Admin y Cartera */}
        <Route
          path="route-collections"
          element={
            <ProtectedRoute requiredRole={['admin', 'cartera']}>
              <RoutePage />
            </ProtectedRoute>
          }
        />

        {/* Auditoría de Cartera - Admin y Cartera */}
        <Route
          path="treasury-audit"
          element={
            <ProtectedRoute requiredRole={['admin', 'cartera']}>
              <TreasuryAuditPage />
            </ProtectedRoute>
          }
        />

        {/* Detalle de Bolsa Interna - Admin y Cartera */}
        <Route
          path="cartera/internal-bag"
          element={
            <ProtectedRoute requiredRole={['admin', 'cartera']}>
              <InternalBagPage />
            </ProtectedRoute>
          }
        />

        {/* Liquidación de Rutas - Admin y Cartera */}
        <Route
          path="cartera/settle-routes"
          element={
            <ProtectedRoute requiredRole={['admin', 'cartera']}>
              <RouteSettlementPage />
            </ProtectedRoute>
          }
        />

        {/* Validar Retornos Físicos - Admin y Cartera */}
        <Route
          path="cartera/validate-returns"
          element={
            <ProtectedRoute requiredRole={['admin', 'cartera']}>
              <ValidateReturnsPage />
            </ProtectedRoute>
          }
        />

        {/* RCs Pendientes - Admin y Cartera */}
        <Route
          path="cartera/pending-rcs"
          element={
            <ProtectedRoute requiredRole={['admin', 'cartera']}>
              <PendingRCsPage />
            </ProtectedRoute>
          }
        />

        {/* Gestión de Caja - Admin y Cartera */}
        <Route
          path="cash-box"
          element={
            <ProtectedRoute requiredRole={['admin', 'cartera']}>
              <CashBoxDashboard />
            </ProtectedRoute>
          }
        />


        {/* Inventario + Facturación Directa - admin y facturador */}
        <Route
          path="inventory-billing"
          element={
            <ProtectedRoute requiredRole={['admin', 'facturador', 'facturacion', 'cartera', 'empacador', 'empaque', 'packaging', 'logistica']}>
              <InventoryBillingPage />
            </ProtectedRoute>
          }
        />

        {/* Gestión de Inventario y Reaprovisionamiento - admin, facturador y cartera */}
        <Route
          path="/inventory-management"
          element={
            <ProtectedRoute requiredRole={['admin', 'cartera', 'facturador', 'facturacion', 'logistica']}>
              <InventoryManagementPage />
            </ProtectedRoute>
          }
        />

        {/* Auditoría de Mezclas A/B - admin */}
        <Route
          path="/mixtures-audit"
          element={
            <ProtectedRoute requiredRole="admin">
              <MixturesAuditPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/reception"
          element={
            <ProtectedRoute>
              <MerchandiseReceptionPage />
            </ProtectedRoute>
          }
        />

        {/* Generador de QR - admin, logística */}
        <Route
          path="qr-generator"
          element={
            <ProtectedRoute requiredRole={['admin', 'logistica']}>
              <QRGeneratorPage />
            </ProtectedRoute>
          }
        />

        {/* Pedidos por Entregar - admin, logística, cartera y facturación */}
        <Route
          path="ready-to-deliver"
          element={
            <ProtectedRoute requiredRole={['admin', 'logistica', 'cartera', 'facturador', 'facturacion']}>
              <ReadyToDeliverPage />
            </ProtectedRoute>
          }
        />

        {/* Despacho de Ruta o Domicilio - admin y logística */}
        <Route
          path="route-dispatch"
          element={
            <ProtectedRoute requiredRole={['admin', 'logistica']}>
              <RouteCreationPage />
            </ProtectedRoute>
          }
        />

        {/* Panel del Mensajero - 3 Vistas */}
        <Route
          path="messenger-dashboard"
          element={
            <ProtectedRoute requiredRole={['admin', 'mensajero']}>
              <MessengerDashboard />
            </ProtectedRoute>
          }
        />

        {/*  My Ruta - Mensajero (Legacy) */}
        <Route
          path="my-route"
          element={
            <ProtectedRoute requiredRole={['admin', 'mensajero']}>
              <Navigate to="/messenger-dashboard" replace />
            </ProtectedRoute>
          }
        />

        {/* --- FLOW DE HANDOFF LOGÍSTICO --- */}
        <Route
          path="logistics/handoff"
          element={
            <ProtectedRoute requiredRole={['admin', 'logistica', 'cartera']}>
              <LogisticsHandoffPage />
            </ProtectedRoute>
          }
        />


        {/* Seguimiento de Mensajeros - admin y cartera */}
        <Route
          path="messenger-tracking"
          element={
            <ProtectedRoute requiredRole={['admin', 'cartera']}>
              <MessengerTrackingPage />
            </ProtectedRoute>
          }
        />

        {/* Rastreo GPS en Vivo - admin, cartera y logística */}
        <Route
          path="live-tracking"
          element={
            <ProtectedRoute requiredRole={['admin', 'cartera', 'logistica']}>
              <LiveTrackingPage />
            </ProtectedRoute>
          }
        />


        {/* Progreso de Empaque - admin, logística y facturador (solo lectura) */}
        <Route
          path="packaging-progress"
          element={
            <ProtectedRoute requiredRole={['admin', 'logistica', 'facturador', 'facturacion', 'cartera']}>
              <PackagingProgressPage />
            </ProtectedRoute>
          }
        />
        {/* Checklist Read-Only por pedido - admin, logística y facturador */}
        <Route
          path="packaging-readonly/:orderId"
          element={
            <ProtectedRoute requiredRole={['admin', 'logistica', 'facturador', 'facturacion', 'cartera']}>
              <PackagingReadOnlyDetailPage />
            </ProtectedRoute>
          }
        />

        {/* Galería de Evidencias de Empaque */}
        <Route
          path="packaging/evidence-gallery"
          element={
            <ProtectedRoute requiredRole={['admin', 'logistica', 'empaque', 'empacador', 'facturador', 'cartera']}>
              <EvidenceGalleryPage />
            </ProtectedRoute>
          }
        />

        {/* Postventa - admin, facturador, cartera, logística */}
        <Route
          path="postventa"
          element={
            <ProtectedRoute requiredRole={['admin', 'facturador', 'cartera', 'logistica']}>
              <PostventaPage />
            </ProtectedRoute>
          }
        />

        {/* Analytics Postventa - admin, logística, cartera */}
        <Route
          path="postventa-analytics"
          element={
            <ProtectedRoute requiredRole={['admin', 'logistica', 'cartera']}>
              <PostventaAnalyticsPage />
            </ProtectedRoute>
          }
        />

        {/* Automatización - solo admin */}
        <Route
          path="automation"
          element={
            <ProtectedRoute requiredRole="admin">
              <AutomationDashboardPage />
            </ProtectedRoute>
          }
        />


        {/* --- PRODUCTOS ESPECIALES & POS INTERNO --- */}
        <Route
          path="admin/inventory/special"
          element={
            <ProtectedRoute requiredRole={['admin', 'cartera', 'facturador', 'facturacion', 'logistica']}>
              <SpecialInventoryPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="admin/pos/internal"
          element={
            <ProtectedRoute requiredRole={['admin', 'cartera', 'facturador', 'facturacion', 'logistica']}>
              <InternalPOSPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="admin/orders/internal-history"
          element={
            <ProtectedRoute requiredRole={['admin', 'cartera', 'facturador', 'facturacion', 'logistica']}>
              <InternalOrdersHistoryPage />
            </ProtectedRoute>
          }
        />

        {/* Cierre Financiero - admin y cartera */}
        <Route
          path="financial-closure"
          element={
            <ProtectedRoute requiredRole={['admin', 'cartera']}>
              <FinancialClosurePage />
            </ProtectedRoute>
          }
        />

        {/* Control de Egresos - admin y cartera */}
        <Route
          path="expenses"
          element={
            <ProtectedRoute requiredRole={['admin', 'cartera']}>
              <ExpensesPage />
            </ProtectedRoute>
          }
        />

        {/* PQR — Peticiones, Quejas y Reclamos */}
        <Route
          path="pqr"
          element={
            <ProtectedRoute requiredRole={['admin', 'cartera', 'facturador', 'facturacion', 'logistica']}>
              <PQRDashboardPage />
            </ProtectedRoute>
          }
        />

        {/* Encuestas de Satisfacción - Resultados */}
        <Route
          path="encuestas"
          element={
            <ProtectedRoute requiredRole={['admin', 'gerente']}>
              <SurveyResultsPage />
            </ProtectedRoute>
          }
        />

        {/* Control Operativo - admin, facturador y cartera */}
        <Route
          path="operational-metrics"
          element={
            <ProtectedRoute requiredRole={['admin', 'facturador', 'facturacion', 'cartera']}>
              <OperationalMetricsPage />
            </ProtectedRoute>
          }
        />

        {/* Control de Dinero (Bancolombia CSV) - admin y cartera */}
        <Route
          path="bank-money-control"
          element={
            <ProtectedRoute requiredRole={['admin', 'cartera']}>
              <BancolombiaMovementsPage />
            </ProtectedRoute>
          }
        />

        {/* Perfil - accesible para todos los roles */}
        <Route path="profile" element={<ProfilePage />} />

        {/* Manual de Usuario - accesible para todos los roles */}
        <Route path="manual" element={<UserManualPage />} />

        {/* --- CONTROL DE ASISTENCIA --- */}
        <Route path="attendance" element={<AttendancePage />} />
        <Route
          path="admin/attendance"
          element={
            <ProtectedRoute requiredRole={['admin']}>
              <AdminAttendancePage />
            </ProtectedRoute>
          }
        />

        {/* --- ANALYTICS DE MARKETING --- */}
        <Route
          path="marketing-analytics"
          element={
            <ProtectedRoute requiredRole={['admin', 'gerente']}>
              <MarketingAnalyticsPage />
            </ProtectedRoute>
          }
        />

        {/* --- CAMPAÑA DE ANIVERSARIO --- */}
        <Route
          path="anniversary-campaign"
          element={
            <ProtectedRoute requiredRole={['admin', 'facturador', 'facturacion']}>
              <AnniversaryCampaignPage />
            </ProtectedRoute>
          }
        />

        {/* --- ALERTAS DE AUDITORÍA --- */}
        <Route
          path="alerts"
          element={
            <ProtectedRoute requiredRole="admin">
              <AlertsPage />
            </ProtectedRoute>
          }
        />

        {/* --- MENSAJEROS --- */}
        <Route
          path="messenger-route"
          element={
            <ProtectedRoute requiredRole={['admin', 'mensajero']}>
              <MessengerRoutePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="messenger-dashboard"
          element={
            <ProtectedRoute requiredRole={['admin', 'mensajero']}>
              <MessengerDashboard />
            </ProtectedRoute>
          }
        />

        {/* Creación de Rutas - Admin y Cartera */}
        <Route
          path="route-creation"
          element={
            <ProtectedRoute requiredRole={['admin', 'cartera']}>
              <RouteCreationPage />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Ruta por defecto */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes >
  );
};

// Componente principal de la aplicación
function App() {
  // Guardia global: ya no deduplicamos en App; el modal maneja el coalescing para evitar duplicados
  useEffect(() => { }, []);

  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <AppRoutes />

          {/* Configuración global de toast */}
          <Toaster
            position="top-right"
            reverseOrder={false}
            gutter={8}
            containerClassName=""
            containerStyle={{}}
            toastOptions={{
              // Configuración por defecto para todos los toast
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },

              // Configuración específica por tipo
              success: {
                duration: 3000,
                style: {
                  background: '#10B981',
                },
                iconTheme: {
                  primary: '#fff',
                  secondary: '#10B981',
                },
              },

              error: {
                duration: 5000,
                style: {
                  background: '#EF4444',
                },
                iconTheme: {
                  primary: '#fff',
                  secondary: '#EF4444',
                },
              },

              loading: {
                duration: Infinity,
                style: {
                  background: '#3B82F6',
                },
              },
            }}
          />
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
